<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { ref } from "vue"
import { useRedirect, type RedirectParams } from "@core/composables"

// ================================
// TYPES & INTERFACES
// ================================
type Props = {
    message: string
    action?: {
        message: string
        type: string
        url?: string
        modal?: string
        freq?: string
    }
    /** Id du bâtiment courant (placeholders `:_id_building` dans l’URL d’action). */
    currentBuildingId?: string | null
    /** Id de la source courante (placeholders `:_id_source` dans l’URL d’action). */
    currentSourceId?: string | null
    closable?: boolean
    severity?: "info" | "warn" | "error"
    /** Désactive le bouton d’action (réessai, redirection, etc.). */
    actionButtonDisabled?: boolean
}

// ================================
// PROPS & EMITS
// ================================
const emit = defineEmits(["click:freq", "click:retry"])
const props = withDefaults(defineProps<Props>(), {
    currentBuildingId: null,
    currentSourceId: null,
    closable: false,
    action: undefined,
    severity: undefined,
    actionButtonDisabled: false,
})

const { redirectWrapper } = useRedirect()

/** Modale support (remplace l’ancien postMessage vers l’app hôte). */
const supportModalVisible = ref(false)

// ================================
// INTERACTIONS
// ================================

/**
 * Gère l'action à effectuer lors du clic sur le bouton d'action.
 */
function onClickActionButton() {
    if (props.actionButtonDisabled) {
        return
    }
    const _id_source = props.currentSourceId ?? undefined
    const _id_building = props.currentBuildingId ?? undefined

    switch (props.action?.type) {
        case "redirect": {
            const url = props.action.url
            if (!url) {
                console.warn("redirect action without url")
                break
            }
            const params: RedirectParams = {}
            if (url.includes(":_id_source")) {
                params._id_source = _id_source
            } else if (url.includes(":_id_building")) {
                params._id_building = _id_building
            }
            redirectWrapper(url, params)
            break
        }
        case "open_modal": {
            supportModalVisible.value = true
            break
        }
        case "change_freq":
            emit("click:freq", props.action?.freq)
            break
        case "retry":
            emit("click:retry")
            break
        default:
            console.warn("Action type not supported")
            break
    }
}
</script>

<template>
    <BaseMessage
        v-bind="$attrs"
        class="data-chart-message"
        :severity="
            severity ? severity : action?.type === 'retry' ? 'warn' : 'info'
        "
        icon="pi pi-info-circle"
        :data-type="action?.type"
        :closable="closable"
    >
        <div class="data-chart-message-content">
            <slot></slot>
            <p
                v-if="!$slots.default && message"
                class="data-chart-list-item__message-content"
                v-html="message"
            ></p>
            <BaseButton
                v-if="action?.message && !$slots.default"
                size="small"
                :disabled="actionButtonDisabled"
                :severity="
                    severity
                        ? severity
                        : action?.type === 'retry'
                          ? 'warn'
                          : 'primary'
                "
                :label="action.message"
                @click="onClickActionButton"
            />
        </div>
    </BaseMessage>
    <SupportModal v-model:visible="supportModalVisible" />
</template>

<style scoped>
.data-chart-message {
    --p-message-content-gap: 1.5rem;
    width: fit-content;
    --p-message-border-width: 1px;
}
.data-chart-message-content {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 1.5rem;
    white-space: pre-line;
    & .p-button {
        margin-left: auto;
    }
}
</style>
